import React, { useEffect } from "react";
import { CloseCircle } from "iconsax-react";
import styled from "styled-components";

import { md3CardRadius } from "../../styles/md3Components";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.5);
  animation: md3OverlayIn 0.2s ease;

  @keyframes md3OverlayIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const Panel = styled.div`
  position: relative;
  width: 100%;
  max-width: 420px;
  max-height: min(90vh, 640px);
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  border-radius: ${({ theme }) => theme.borderRadius.xl}px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.md3Level3};
  animation: md3PanelIn 0.25s
    ${({ theme }) => theme.md3.motion.easing.expressive};
  box-sizing: border-box;

  @keyframes md3PanelIn {
    from {
      opacity: 0;
      transform: scale(0.94) translateY(8px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  /* Evita barra de scroll fantasma em alertas curtos */
  scrollbar-gutter: auto;
`;

const CloseRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 8px 8px 0;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 9999px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  transition: opacity 0.15s ease, background 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.hover};
  }

  &:active {
    opacity: 0.7;
  }
`;

export type ModalShellProps = {
  open: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  className?: string;
  maxWidth?: number;
  "aria-label"?: string;
};

export function ModalShell({
  open,
  onClose,
  children,
  className,
  maxWidth = 420,
  "aria-label": ariaLabel,
}: ModalShellProps) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <Overlay
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <Panel
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className={className}
        style={{ maxWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        {onClose ? (
          <CloseRow>
            <CloseButton type="button" aria-label="Fechar" onClick={onClose}>
              <CloseCircle size={22} variant="Linear" />
            </CloseButton>
          </CloseRow>
        ) : null}
        {children}
      </Panel>
    </Overlay>
  );
}

/** Painel maior para alertas (CustomModal / ValidationModal). */
export const ModalAlertPanel = styled.div`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 8px 32px 32px;
  border-radius: ${({ theme }) => md3CardRadius(theme)}px;
`;
